import { useDispatch, useSelector } from "react-redux";
import { showNotification } from "../../../shared/utils/utils";
import { RootState } from "../../../shared/types/common";
import { Drawer, Upload, UploadFile } from "antd";
import GenericFormComponent from "../../../shared/components/generic-form/generic-form.component";
import { useForm } from "antd/lib/form/Form";
import { useEffect, useState } from "react";
import { Client } from "../../../core/api-client";
import { API_GET_ALL_STAFF_LIST, API_GET_DOCUMENT_SIGNED_URL } from "../../../shared/routes/private.routes";
import {
  createVendor,
  updateVendorById,
} from "../../../core/store/reducers/vendor.slice";
import { InboxOutlined } from "@ant-design/icons";
import {
  getDocuments,
  getSignedUrl,
} from "../../../core/store/reducers/activity-log.slice";
import { requestGetByModuleCommon } from "../../../core/store/reducers/master.slice";

interface Props {
  onClose: () => void;
  visible: boolean;
  selectedEntity?: any;
  onSubmit?: () => void;
}
const CreateEditVendorComponent = ({
  onClose,
  visible,
  selectedEntity = null,
  onSubmit = () => {
    return;
  },
}: Props) => {
  const dispatch = useDispatch();
  const [form] = useForm();
  const { user } = useSelector((state: RootState) => state.auth);
  const { screenType } = useSelector((state: RootState) => state.settings);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [fileList, setFileList] = useState<UploadFile<any>[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [serviceTypes, setServiceTypes] = useState<any[]>([]);

  const onFinish = (values: any) => {
    // Split fileList into two groups
    const oldFiles = fileList.filter((file) => !file.originFileObj); // previously uploaded
    const newFiles = fileList.filter((file) => file.originFileObj); // new uploads
    const payload = {
      vendorName: values?.vendorName,
      companyEmail: values?.companyEmail,
      companyPhone: values?.companyPhone,
      companyAddress: values?.companyAddress,
      serviceType: values?.serviceType
    };
    const formData = new FormData();

    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value);
      }
    });
     // Append file (assuming single file upload)
     if (fileList && newFiles.length > 0) {
      newFiles.forEach((fileItem) => {
        const file = fileItem;
        formData.append("files", file.originFileObj as File); // use consistent key name
      });
    }
    const remainingOldFiles = oldFiles.map((file) => ({
      uid: file.uid,
      name: file.name,
      url: file.url,
    }));
    formData.append("oldFiles", JSON.stringify(remainingOldFiles));
    if (selectedEntity) {
      dispatch(
        updateVendorById({ vendorId: selectedEntity?.vendorId, formData })
      ).then((val: any) => {
        if (val?.payload?.data) {
          showNotification("Vendor Details Updated Successfully", "success");
          onSubmit();
        }
      });
    } else {
      dispatch(createVendor(formData)).then((val: any) => {
        if (val?.payload?.data) {
          showNotification("New Vendor Created Successfully", "success");
          onSubmit();
        }
      });
    }
  };

  const fetchStaffList = async () => {
    const response = await Client.get(API_GET_ALL_STAFF_LIST);
    if (response?.data?.data) {
      setStaffList(response?.data?.data);
    } else {
      setStaffList([]);
    }
  };

  useEffect(() => {
    if (documents?.length) {
      const converted = documents.map((doc) => ({
        uid: doc.id,
        name: doc.originalFileName,
        status: "done",
        url: doc.bucketPath || "",
      }));

      // Optional: prevent unnecessary state update
      setFileList((prev: any) => {
        const same = JSON.stringify(prev) === JSON.stringify(converted);
        return same ? prev : converted;
      });
    } else {
      setFileList([]); // Clear fileList if documents are empty
    }
  }, [documents]);

  const fetchDocumentList = async () => {
    if (!selectedEntity?.vendorId) {
      return;
    }
    try {
      const response = await dispatch(
        getDocuments(selectedEntity?.vendorId)
      ).unwrap();
      setDocuments(response?.data || []);
    } catch (error) {
      setDocuments([]);
    }
  };
  useEffect(() => {
    if (selectedEntity) {
      form.setFieldsValue({
        vendorName: selectedEntity?.vendorName,
        companyEmail: selectedEntity?.companyEmail,
        companyPhone: selectedEntity?.companyPhone,
        companyAddress: selectedEntity?.companyAddress,
        serviceType: selectedEntity?.serviceType
      });
    }
  }, [form, selectedEntity]);

  useEffect(() => {
    fetchStaffList();
    fetchDocumentList();
    fetchServiceTypes();
  }, []);

  const fetchServiceTypes = async () => {
    try {
      const response = await dispatch(
        requestGetByModuleCommon({ module: "master_service_type" })
      ).unwrap();
      // console.log(response.data);
      if (response?.success) {
        setServiceTypes(response?.data.items);
      }
    } catch (error) {}
  };
  const dropdownOptions = staffList.map((staff) => ({
    value: staff?.staffId, // Assuming 'id' is the unique identifier
    label: staff?.staffName, // Assuming 'name' is the display name
  }));

  const onFinishFailed = (_errorInfo: any) => {
    return;
  };

  const onValuesChange = (value: any, _allValues: any) => {};

  const nameValidationRule = (fieldName: string) => ({
    pattern: /^[A-Za-z\s.'/-]+$/,
    message: `Please enter a valid ${fieldName}. Only letters, spaces, apostrophes ('), dots (.), hyphens (-), and slashes (/) are allowed.`,
  });

  const handleDownload = async (file: any) => {
         console.log("file", file);
      if (!file?.uid) {
        console.log("Document ID is not available");
        return;
      }
    
      try {
        const response = await Client.get(
          `${API_GET_DOCUMENT_SIGNED_URL}${file.uid}`
        );
    
        const downloadUrl =
          response?.data?.data?.url || response?.data?.url;
    
        if (!downloadUrl) {
          console.log("Download URL is not available");
          return;
        }
    
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.download =
          response?.data?.data?.fileName ||
          file.name ||
          "document";
    
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Failed to download document:", error);
      }
    };

  const handleUploadChange = (info: any) => {
    const { file, fileList } = info;

    // Optional: filter duplicates by UID or name
    const uniqueFileList = fileList.filter(
      (file: { uid: any }, index: any, self: any[]) =>
        index === self.findIndex((f) => f.uid === file.uid)
    );
    // .map((file: { originFileObj: any; }) => file.originFileObj);

    setFileList(uniqueFileList);

    if (file.status === "done" || !file.status) {
      showNotification(`${file.name} file uploaded successfully.`, "success");
    } else if (file.status === "error") {
      showNotification(`${file.name} file upload failed.`, "error");
    }
  };

  const handleRemove = (file: any) => {
    // const updatedList = fileList.filter((f) => f.uid !== file.uid);
    // setFileList(updatedList);
  };
  const handleCustomRequest = ({ file, onSuccess }: any) => {
    // Simulate upload success after 0ms
    setTimeout(() => {
      onSuccess("ok");
    }, 0);
  };

  const beforeUpload = async (file: any) => {
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ];
  
    const isValidType = allowedTypes.includes(file.type);
  
    if (!isValidType) {
      showNotification("Only PDF or Word files are allowed", "warning");
      return Upload.LIST_IGNORE;
    }
  
    return false; // Prevent auto upload if you're using customRequest
  };
  var colSpan = screenType.isPortrait ? 24 : 12;
  const formProps = {
    form: form,
    name: "creat-edit-vendor",
    heading: selectedEntity ? "Edit Vendor Details" : "Add New Vendor",
    initialValues: {},
    formElements: [
      {
        name: "vendorName",
        colSpan: 12,
        label: "Vendor Name",
        placeholder: "Enter Vendor Name ",
        type: "Input",
        allowClear: true,
        rules: [
          { required: true, message: "Please enter Vendor Name" },
          nameValidationRule("Vendor Name"),
        ],
      },
      {
        name: "companyEmail",
        colSpan: 12,
        label: "Company Email",
        placeholder: "Enter Company Email",
        type: "Input",
        allowClear: true,
        rules: [
          { required: true, message: "Please enter Company Email" },
          { type: "email", message: "Please enter a valid email address" },
        ],
      },
      {
        name: "companyPhone",
        colSpan: 12,
        label: "Company Phone",
        placeholder: "Enter Company Phone",
        type: "Input",
        allowClear: true,
        maxLength: 10,
        rules: [{ required: true, message: "Please enter Company Phone" }],
        fieldProps: {
          onChange: (e: any) => {
            const onlyNums = e.target.value.replace(/\D/g, "").slice(0, 10);
            e.target.value = onlyNums; // optional - doesn't control input
            e.currentTarget.value = onlyNums; // for React event
          },
          inputMode: "numeric", // mobile-friendly numeric keypad
          onKeyDown: (e: any) => {
            // Prevent typing non-numeric characters
            if (
              !/[0-9]/.test(e.key) &&
              e.key !== "Backspace" &&
              e.key !== "Delete" &&
              e.key !== "ArrowLeft" &&
              e.key !== "ArrowRight"
            ) {
              e.preventDefault();
            }
          },
        },
      },

      {
        name: "companyAddress",
        colSpan: 12,
        label: "Company Address",
        placeholder: "Enter Company Address",
        type: "Input",
        allowClear: true,
        rules: [{ required: true, message: "Please enter Company Address" }],
      },
      {
        name: "serviceType",
        label: "Service Type",
        placeholder: "Service Type",
        type: "Select",
        rules: [{ required: false, message: "Please enter Service Type" }],
        colSpan: colSpan,
        showSearch: true,
        allowClear: true,
        options: serviceTypes?.map((type) => ({
          value: type?.id,
          label: type?.name,
        })),
      },
      {
        name: "files", // or any identifier
        label: "Upload File",
        type: "Custom",
        colSpan: 16,
        // The dragger is driven by the `fileList` state below, not by the form
        // store, so it is not wrapped in its own Form.Item — an inner Form.Item
        // would inject `fileList` onto this plain div and React would warn
        // about an unrecognised DOM prop.
        render: ({} = {}) => (
            <div style={{ maxHeight: 400 }}>
              <Upload.Dragger
                name="file"
                multiple={true}
                listType="text"
                showUploadList={{ showRemoveIcon: true }}
                fileList={fileList}
                onChange={handleUploadChange}
                onRemove={handleRemove}
                customRequest={handleCustomRequest}
                beforeUpload={beforeUpload}
                accept=".pdf,.doc,.docx"
                style={{ padding: "16px 0", backgroundColor: "#dcdcdc" }}
                // onPreview={async (file) => {
                //   try {
                //     if (file.url) {
                //       const res = await dispatch(
                //         getSignedUrl({
                //           module: "vendor",
                //           params: { url: file.url },
                //         })
                //       ).unwrap();

                //       if (res?.data?.url) {
                //         window.open(res.data.url, "_blank");
                //       }
                //     }
                //   } catch (e) {
                //     console.log("Could not open file");
                //   }
                // }}
                onPreview={async (file) => {
                  await handleDownload(file);
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  Click or drag file to this area to upload
                </p>
              </Upload.Dragger>
            </div>
        ),
      },
    ],
    extraButtons: [
      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Cancel",
        onClick: onClose,
      },
      {
        htmlType: "button",
        colSpan: 5,
        loading: false,
        submitButtonText: "Clear",
        type: "clearForm",
      },
      {
        htmlType: "submit",
        colSpan: 5,
        loading: false,
        submitButtonText: "Save",
        submitButtonType: "primary",
      },
    ],
    onFinish: onFinish,
    onFinishFailed: onFinishFailed,
    onValuesChange: onValuesChange,
  };
  return (
    <>
      <Drawer
        maskClosable={false}
        title={<strong>{formProps.heading}</strong>}
        className="form-drawer"
        width={
          screenType.isPortrait
            ? "90%"
            : screenType.isLg || screenType.isXl
            ? 720
            : 500
        }
        onClose={onClose}
        open={visible}
      >
        <GenericFormComponent {...formProps} />
      </Drawer>
    </>
  );
};

export default CreateEditVendorComponent;
